import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive, Pagination } from "@/pages/Senders";
import { StatusBadge } from "@/components/StatusBadge";
import { PaperPlaneTilt, Sparkle, Eye, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { useAiModels, ModelOptions, defaultModelKey } from "@/lib/aiModels";

export default function TestSend() {
  const [senders, setSenders] = useState([]);
  const [sendersTotal, setSendersTotal] = useState(0);
  const [sendersPage, setSendersPage] = useState(0);
  const [sendersPageSize, setSendersPageSize] = useState(50);
  const [senderSearch, setSenderSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [providerFilter, setProviderFilter] = useState("");
  const [proxyFilter, setProxyFilter] = useState("valid");
  const [emailsText, setEmailsText] = useState("");
  const [validEmails, setValidEmails] = useState([]);
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [subject, setSubject] = useState("[TEST] deliverability check");
  const [textBody, setTextBody] = useState("Hi there,\n\nThis is a test.\n\nRegards\n[senderFullName]");
  const [contentMode, setContentMode] = useState("manual");
  const [aiInstructions, setAiInstructions] = useState("");
  const { models: aiModels } = useAiModels();
  const [aiModel, setAiModel] = useState("");
  useEffect(() => { if (!aiModel && aiModels.length) setAiModel(defaultModelKey(aiModels)); }, [aiModels]); // eslint-disable-line
  const [aiThinking, setAiThinking] = useState("");
  const [sample, setSample] = useState({
    firstName: "Alex", lastName: "Kim",
    loanAmount: "$18,500", phone: "(555) 123-4567",
    ctaLink: "https://example.com/review/abc123",
    companyName: "", city: "",
  });
  const [lengths, setLengths] = useState({
    subject_min_chars: 40, subject_max_chars: 60,
    body_min_paragraphs: 4, body_max_paragraphs: 6,
  });
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [expandedId, setExpandedId] = useState(null);
  const [clearingHistory, setClearingHistory] = useState(false);

  const loadSenders = async () => {
    const params = { limit: sendersPageSize, skip: sendersPage * sendersPageSize };
    if (providerFilter) params.provider = providerFilter;
    if (proxyFilter) params.proxy_status = proxyFilter;
    if (senderSearch.trim()) params.search = senderSearch.trim();
    const r = await api.get("/senders", { params });
    setSenders(r.data.senders);
    setSendersTotal(r.data.total || 0);
  };
  const loadLogs = async () => {
    const r = await api.get("/test-send/logs", {
      params: { limit: logsPageSize, skip: logsPage * logsPageSize },
    });
    setLogs(r.data.logs);
    setLogsTotal(r.data.total || 0);
  };
  useEffect(() => { loadSenders(); /* eslint-disable-next-line */ }, [providerFilter, proxyFilter, sendersPage, sendersPageSize]);
  // Debounced search — reset to page 0 and reload when the user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => {
      setSendersPage(0);
      loadSenders();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [senderSearch]);
  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [logsPage, logsPageSize]);

  const parse = async () => {
    const r = await api.post("/test-send/parse-emails", { text: emailsText });
    setValidEmails(r.data.valid);
    setInvalidEmails(r.data.invalid);
  };

  const submitTest = async (previewOnly) => {
    if (contentMode === "ai_studio" && previewOnly) {
      // Preview: need at least 1 sender (for identity) — recipient can be a placeholder
      if (selected.size === 0) {
        toast.error("Select at least 1 sender to use their identity");
        return;
      }
    } else {
      if (selected.size === 0 || validEmails.length === 0) {
        toast.error("Select at least 1 sender and enter 1+ valid recipient email");
        return;
      }
    }
    setRunning(true);
    try {
      const payload = {
        sender_ids: Array.from(selected),
        recipient_emails: previewOnly && validEmails.length === 0
          ? ["preview@example.com"]
          : validEmails,
        subject: contentMode === "ai_studio" ? "" : subject,
        text_body: contentMode === "ai_studio" ? "" : textBody,
        html_body: "",
        content_mode: contentMode,
        ai_instructions: aiInstructions,
        ai_model: aiModel,
        ai_thinking: aiThinking,
        sample_values: sample,
        subject_min_chars: Number(lengths.subject_min_chars) || 40,
        subject_max_chars: Number(lengths.subject_max_chars) || 60,
        body_min_paragraphs: Number(lengths.body_min_paragraphs) || 4,
        body_max_paragraphs: Number(lengths.body_max_paragraphs) || 6,
        preview_only: !!previewOnly,
      };
      const r = await api.post("/test-send", payload);
      setResults(r.data.results);
      if (previewOnly) {
        toast.success(`Generated ${r.data.results.length} preview${r.data.results.length !== 1 ? "s" : ""} — click a row to expand`);
      } else {
        const ok = r.data.results.filter((x) => x.status === "provider_accepted").length;
        toast.success(`Test done · ${ok}/${r.data.results.length} accepted`);
      }
      loadLogs();
      setLogsPage(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setRunning(false); }
  };

  const toggleAll = () => {
    // "Select all" here means all senders CURRENTLY visible on this page.
    // We keep prior-page selections intact so the operator can build a
    // cross-page selection by paging through and ticking each page.
    const pageIds = senders.map((s) => s.id);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const n = new Set(selected);
    if (allOnPageSelected) {
      pageIds.forEach((id) => n.delete(id));
    } else {
      pageIds.forEach((id) => n.add(id));
    }
    setSelected(n);
  };

  const clearHistory = async () => {
    if (!confirm("Delete every test-send history row? This cannot be undone.")) return;
    setClearingHistory(true);
    try {
      const r = await api.delete("/test-send/logs");
      toast.success(`Cleared ${r.data.deleted} test log${r.data.deleted === 1 ? "" : "s"}`);
      setLogsPage(0);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setClearingHistory(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="micro mb-1">Operations</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Test Send</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="micro mb-2">1. Select senders</div>
          <div className="flex gap-2 mb-3 flex-wrap">
            <select value={providerFilter} onChange={(e) => { setSendersPage(0); setProviderFilter(e.target.value); }}
              data-testid="test-filter-provider"
              className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white">
              <option value="">any provider</option>
              <option value="google">google</option>
              <option value="microsoft">microsoft</option>
            </select>
            <select value={proxyFilter} onChange={(e) => { setSendersPage(0); setProxyFilter(e.target.value); }}
              data-testid="test-filter-proxy"
              className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white">
              <option value="">any proxy</option>
              <option value="valid">valid</option>
              <option value="not_tested">not tested</option>
            </select>
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                type="text"
                value={senderSearch}
                onChange={(e) => setSenderSearch(e.target.value)}
                placeholder="search email or name..."
                data-testid="test-sender-search"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] pl-7 pr-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none"
              />
            </div>
          </div>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] max-h-96 overflow-y-auto">
            <table className="dtable w-full">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>
                    <input type="checkbox"
                      checked={senders.length > 0 && senders.every((s) => selected.has(s.id))}
                      onChange={toggleAll} data-testid="test-select-all" />
                  </th>
                  <th>Email</th><th>Proxy</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {senders.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-[#71717A] py-6 font-mono">[ no senders match ]</td></tr>
                )}
                {senders.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <input type="checkbox" checked={selected.has(s.id)}
                        onChange={(e) => {
                          const n = new Set(selected);
                          e.target.checked ? n.add(s.id) : n.delete(s.id);
                          setSelected(n);
                        }} />
                    </td>
                    <td className="font-mono text-xs">{s.email}</td>
                    <td className="font-mono text-xs">{s.proxy_host || "—"}</td>
                    <td><StatusBadge status={s.proxy_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={sendersPage}
            pageSize={sendersPageSize}
            total={sendersTotal}
            onPageChange={setSendersPage}
            onPageSizeChange={(s) => { setSendersPageSize(s); setSendersPage(0); }}
            sizeOptions={[25, 50, 200]}
            testidPrefix="test-senders-pg"
          />
          <div className="micro mt-2 text-[#71717A]" data-testid="test-selected-count">
            {selected.size} selected across all pages · {sendersTotal} total match filters
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="micro mb-2">2. Test recipients</div>
            <textarea rows={4} value={emailsText} onChange={(e) => setEmailsText(e.target.value)}
              onBlur={parse}
              data-testid="test-recipients-text"
              placeholder="one email per line, or comma-separated"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            <div className="flex gap-4 text-xs mt-1">
              <span className="text-[#00FF66] font-mono">{validEmails.length} valid</span>
              <span className="text-[#FF3B30] font-mono">{invalidEmails.length} invalid</span>
              <span className="text-[#A1A1AA] font-mono">{selected.size * validEmails.length} total sends</span>
            </div>
          </div>

          <div>
            <div className="micro mb-2">3. Content</div>
            <div className="flex gap-2 mb-2">
              {[
                { id: "manual", label: "manual" },
                { id: "spintax", label: "spintax" },
                { id: "ai_studio", label: "ai studio" },
              ].map((m) => (
                <button key={m.id} onClick={() => setContentMode(m.id)} data-testid={`test-mode-${m.id}`}
                  className={`px-3 py-1 text-xs uppercase font-mono tracking-wider inline-flex items-center gap-1 ${contentMode === m.id ? "bg-[#E1FF00] text-black" : "border border-[#2A2A2A] text-[#A1A1AA]"}`}>
                  {m.id === "ai_studio" && <Sparkle size={11} weight="fill" />} {m.label}
                </button>
              ))}
            </div>

            {contentMode === "ai_studio" ? (
              <div className="space-y-2 border border-[#00E5FF]/30 bg-[#0A0A0A] p-3">
                <div className="text-[10px] text-[#00E5FF] font-mono uppercase tracking-wider">
                  AI Studio · random spec per email
                </div>
                <p className="text-[11px] text-[#A1A1AA] leading-snug">
                  Each email is generated fresh — 16 intents × 15 support tones × 22 CTAs × 18 subject tones.
                  Placeholders (<span className="font-mono text-[#E1FF00]">[loanAmount] [firstName] [phone] [ctaLink] [senderFullName]</span>)
                  are emitted verbatim and substituted with the sample values below.
                </p>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["firstName", "First name"],
                    ["lastName", "Last name"],
                    ["loanAmount", "Amount"],
                    ["phone", "Phone"],
                    ["ctaLink", "CTA link"],
                    ["companyName", "Company"],
                  ].map(([k, label]) => (
                    <label key={k} className="block">
                      <span className="micro block mb-0.5 text-[#71717A]">{label}</span>
                      <input value={sample[k] || ""}
                        onChange={(e) => setSample({ ...sample, [k]: e.target.value })}
                        data-testid={`test-sample-${k}`}
                        className="w-full bg-black border border-[#2A2A2A] px-2 py-1 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
                    </label>
                  ))}
                </div>

                <div className="pt-2 border-t border-[#2A2A2A]">
                  <div className="text-[10px] text-[#00E5FF] font-mono uppercase tracking-wider mb-1">
                    Length constraints
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      ["subject_min_chars", "Subj min (chars)"],
                      ["subject_max_chars", "Subj max (chars)"],
                      ["body_min_paragraphs", "Body min (para)"],
                      ["body_max_paragraphs", "Body max (para)"],
                    ].map(([k, label]) => (
                      <label key={k} className="block">
                        <span className="micro block mb-0.5 text-[#71717A]">{label}</span>
                        <input type="number" min={1} value={lengths[k]}
                          onChange={(e) => setLengths({ ...lengths, [k]: e.target.value })}
                          data-testid={`test-length-${k}`}
                          className="w-full bg-black border border-[#2A2A2A] px-2 py-1 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#71717A] mt-1 leading-snug">
                    Subject placeholders auto-cycle across every 10 emails:{" "}
                    <span className="font-mono text-[#E1FF00]">[loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> · plain ·
                    <span className="font-mono text-[#E1FF00]"> [loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> · <span className="font-mono text-[#E1FF00]">[phone]</span> · plain ·
                    <span className="font-mono text-[#E1FF00]"> [loanAmount]+[firstName]</span> · <span className="font-mono text-[#E1FF00]">[firstName]</span> · plain
                  </div>
                </div>

                <label className="block">
                  <span className="micro block mb-0.5 text-[#71717A]">AI model</span>
                  <select value={aiModel} onChange={(e) => setAiModel(e.target.value)}
                    data-testid="test-ai-model"
                    className="w-full bg-black border border-[#2A2A2A] px-2 py-1 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none">
                    <ModelOptions models={aiModels} />
                  </select>
                </label>

                <label className="block">
                  <span className="micro block mb-0.5 text-[#71717A]">Thinking mode</span>
                  <select value={aiThinking} onChange={(e) => setAiThinking(e.target.value)}
                    data-testid="test-ai-thinking"
                    className="w-full bg-black border border-[#2A2A2A] px-2 py-1 text-[11px] text-white font-mono focus:border-[#E1FF00] focus:outline-none">
                    <option value="">Default (model decides)</option>
                    <option value="disabled">Disabled (fast · ~2-5s)</option>
                    <option value="enabled">Enabled (reasoning · ~15-30s)</option>
                  </select>
                </label>

                <label className="block">
                  <span className="micro block mb-0.5 text-[#71717A]">AI instructions (optional)</span>
                  <textarea rows={2} value={aiInstructions}
                    onChange={(e) => setAiInstructions(e.target.value)}
                    data-testid="test-ai-instructions"
                    placeholder="Extra guidance for the model. Leave blank to use the spec as-is."
                    className="w-full bg-black border border-[#2A2A2A] px-2 py-1 text-[11px] text-white focus:border-[#E1FF00] focus:outline-none" />
                </label>
              </div>
            ) : (
              <>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="test-subject"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white mb-2 focus:border-[#E1FF00] focus:outline-none" />
                <textarea rows={6} value={textBody} onChange={(e) => setTextBody(e.target.value)} data-testid="test-body"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
              </>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {contentMode === "ai_studio" && (
              <BtnSecondary onClick={() => submitTest(true)} testid="test-preview-ai" disabled={running}>
                <Eye size={12} weight="fill" /> {running ? "GENERATING..." : "Preview generation"}
              </BtnSecondary>
            )}
            <BtnPrimary onClick={() => submitTest(false)} testid="test-run" disabled={running}>
              <PaperPlaneTilt size={12} weight="fill" /> {running ? "SENDING..." : "Run test send"}
            </BtnPrimary>
          </div>
        </div>
      </div>

      {results.length > 0 && (
        <div className="mt-8">
          <div className="micro mb-2">Latest results</div>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full" data-testid="test-results-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th>Sender</th><th>Recipient</th><th>Subject</th><th>Status</th><th>Error / response</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const detail = r.error_message || r.proxy_error_message || r.provider_response || "";
                  const isError = !!(r.error_message || r.proxy_error_message);
                  const expandable = !!(r.generated_body || (r.spec_used && Object.keys(r.spec_used).length));
                  const isOpen = expandedId === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr>
                        <td>
                          {expandable && (
                            <button
                              data-testid={`expand-${r.id}`}
                              onClick={() => setExpandedId(isOpen ? null : r.id)}
                              className="text-[#00E5FF] font-mono text-xs w-5">
                              {isOpen ? "−" : "+"}
                            </button>
                          )}
                        </td>
                        <td className="font-mono text-xs">{r.sender_email}</td>
                        <td className="font-mono text-xs">{r.recipient_email}</td>
                        <td className="font-mono text-xs truncate max-w-xs">{r.subject}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td className={`font-mono text-xs whitespace-pre-wrap ${isError ? "!text-[#FF3B30]" : "!text-[#71717A]"}`}
                            data-testid={`result-detail-${r.id}`}>
                          {detail || <span className="text-[#71717A]">(no message returned)</span>}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`preview-body-${r.id}`}>
                          <td></td>
                          <td colSpan={5} className="!bg-[#050505] !px-4 !py-3">
                            {r.spec_used && Object.keys(r.spec_used).length > 0 && (
                              <div className="mb-2 flex flex-wrap gap-1">
                                {Object.entries(r.spec_used).map(([k, v]) => (
                                  <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 border border-[#00E5FF]/30 text-[#00E5FF]">
                                    {k}: {String(v)}
                                  </span>
                                ))}
                              </div>
                            )}
                            <pre className="whitespace-pre-wrap font-mono text-[11px] text-[#E4E4E7] leading-relaxed">
{r.generated_body}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="flex items-center justify-between mb-2">
          <div className="micro">Test history <span className="text-[#71717A] normal-case">({logsTotal})</span></div>
          <BtnDestructive onClick={clearHistory} disabled={clearingHistory || logsTotal === 0} testid="test-history-clear">
            <Trash size={12} weight="bold" /> {clearingHistory ? "Clearing..." : "Clear history"}
          </BtnDestructive>
        </div>
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
          <table className="dtable w-full" data-testid="test-history-table">
            <thead><tr><th>When</th><th>Sender</th><th>To</th><th>Subject</th><th>Status</th><th>Error / response</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={6} className="text-center text-[#71717A] py-6 font-mono">[ no test history ]</td></tr>}
              {logs.map((l) => {
                const detail = l.error_message || l.proxy_error_message || l.provider_response || "";
                const isError = !!(l.error_message || l.proxy_error_message);
                return (
                  <tr key={l.id}>
                    <td className="font-mono text-xs text-[#71717A]">{l.created_at?.slice(0, 19)}</td>
                    <td className="font-mono text-xs">{l.sender_email}</td>
                    <td className="font-mono text-xs">{l.recipient_email}</td>
                    <td className="truncate max-w-xs">{l.subject}</td>
                    <td><StatusBadge status={l.status} /></td>
                    <td className={`font-mono text-[11px] truncate max-w-md ${isError ? "!text-[#FF3B30]" : "!text-[#71717A]"}`}>
                      {detail || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          page={logsPage}
          pageSize={logsPageSize}
          total={logsTotal}
          onPageChange={setLogsPage}
          onPageSizeChange={(s) => { setLogsPageSize(s); setLogsPage(0); }}
          sizeOptions={[25, 50, 200]}
          testidPrefix="test-history-pg"
        />
      </div>
    </div>
  );
}
